import { useState, useEffect } from 'react';
import { Button, Card, Form, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { supabase } from '../../lib/supabase';
import { DonationHeader } from './DonationHeader';
import { DonationFooter } from './DonationFooter';
import { PasswordInput } from './PasswordInput';

function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [hasSession, setHasSession] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setHasSession(!!data?.session);
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw new Error(error.message);
      setIsComplete(true);
      toast.success('Password updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (hasSession === null) {
    return (
      <div className="bg-wr-donation-grey-50">
        <DonationHeader />
        <div className="container py-6 d-flex justify-content-center">
          <Spinner animation="border" />
        </div>
        <DonationFooter />
      </div>
    );
  }

  if (!hasSession) {
    return (
      <div className="bg-wr-donation-grey-50">
        <DonationHeader />
        <div className="container py-6">
          <div className="row justify-content-center">
            <div className="col-12 col-md-6 col-lg-5">
              <Card className="text-center py-5">
                <Card.Body>
                  <h4 className="mb-3">Invalid or Expired Link</h4>
                  <p className="text-muted">This password reset link is invalid or has expired. Please request a new one.</p>
                  <Button
                    className="bg-wr-donation-primary border-0 mt-3"
                    onClick={() => window.location.href = '/'}
                  >
                    Back to Home
                  </Button>
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>
        <DonationFooter />
      </div>
    );
  }

  return (
    <div className="bg-wr-donation-grey-50">
      <DonationHeader />
      <div className="container py-6">
        <div className="row justify-content-center">
          <div className="col-12 col-md-6 col-lg-5">
            {isComplete ? (
              <Card className="text-center py-5">
                <Card.Body>
                  <h4 className="mb-3">Password Updated</h4>
                  <p className="text-muted">Your password has been reset successfully.</p>
                  <Button
                    className="bg-wr-donation-primary border-0 mt-3"
                    onClick={() => window.location.href = '/'}
                  >
                    Continue to Donate
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <Card>
                <Card.Body>
                  <h4 className="mb-3 text-center">Set New Password</h4>
                  {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>New Password</Form.Label>
                      <PasswordInput
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Enter new password"
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Confirm New Password</Form.Label>
                      <PasswordInput
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                      />
                    </Form.Group>
                    <Button
                      type="submit"
                      className="w-100 bg-wr-donation-primary border-0 py-2"
                      disabled={isLoading}
                    >
                      {isLoading ? <Spinner size="sm" /> : 'Update Password'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            )}
          </div>
        </div>
      </div>
      <DonationFooter />
    </div>
  );
}

export { ResetPasswordPage };
